import { create } from 'zustand';

/**
 * Light/dark theme state. Class-based switching per apps/web/CLAUDE.md
 * §8 — `applyThemeClass` toggles `.dark` on `<html>`, which flips the
 * OKLCH token set defined in globals.css.
 *
 * Default is the OS preference (`prefers-color-scheme`); once the user
 * toggles, the explicit choice is persisted to localStorage and wins
 * on subsequent loads.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'mailchute-theme';

/**
 * Resolve the boot theme: an explicit stored choice if present, else
 * the OS preference, else light. Guards `matchMedia` so it's safe in
 * environments that don't implement it.
 */
export function resolveInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/** Toggle the `.dark` class on the document root to match `theme`. */
export function applyThemeClass(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

interface ThemeState {
  readonly theme: Theme;
  readonly toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: resolveInitialTheme(),
  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem(STORAGE_KEY, next);
    applyThemeClass(next);
    set({ theme: next });
  },
}));
